import useCanSeeQuizStandards from "@/assessments/utils/useCanSeeQuizStandards";
import useCanSeeReadingSkills from "@/assessments/utils/useCanSeeReadingSkills";
import useContentMetadataGet from "@/content/useContentMetadataGet";
import { AttachmentQuestion } from "@/types/models";
import usePresentModeContext from "@/view/utils/usePresentModeContext";
import PowerWordsScoreGraphics from "../PowerWordsContainer/PowerWordsScoreGraphic/PowerWordsScoreGraphic";
import ProgressBar from "../ProgressBar";
import QuizElaStandardsSection from "../QuizElaStandardsSection";
import {
  countContainerCss,
  countCss,
  currentItemCss,
  labelCss,
  progressBarContainerCss,
  questionCountContainerCss,
  readingSkillCss,
  rootCss,
  scoreIllustrationContainerCss,
  scoreLabelCss,
  textContainerCss,
} from "./ItemCount.style";

interface ItemCountProps {
  count: number;
  currentItem: number;
  hasScore?: boolean;
  isCompact?: boolean;
  isSubmitted?: boolean;
  item?: string;
  powerWordsPoints?: number;
  question?: AttachmentQuestion;
  questionCounterTitleRef?: React.LegacyRef<HTMLHeadingElement>;
  title?: string;
}

const ItemCount = ({
  count = 4,
  currentItem = 1,
  hasScore = false,
  isCompact = false,
  isSubmitted = false,
  item = "Question",
  powerWordsPoints,
  question,
  questionCounterTitleRef,
  title = "Score",
}: ItemCountProps) => {
  const quizQuestionContentId = question?.id ?? null;
  const { data: metadata } = useContentMetadataGet(quizQuestionContentId);
  const canSeeStandards = useCanSeeQuizStandards();
  const canSeeReadingSkills = useCanSeeReadingSkills();

  const { isPresentModeActive } = usePresentModeContext();

  const renderStandards = () => {
    const shouldRenderStandards =
      canSeeStandards && metadata && !isPresentModeActive;
    if (!shouldRenderStandards) return;

    const activityStandard =
      metadata?.find(({ data }) => data.content_type === "STANDARD") || null;
    if (!activityStandard) return null;

    return <QuizElaStandardsSection standards={activityStandard} />;
  };

  const renderReadingSkill = () => {
    const shouldRenderReadingSkill =
      canSeeReadingSkills && metadata && !isPresentModeActive;
    if (!shouldRenderReadingSkill) return;

    const readingSkill =
      metadata?.find(({ data }) => data.content_type === "READING_SKILLS") ||
      null;
    if (!readingSkill) return null;

    return (
      <div css={readingSkillCss}>
        <h3>Reading Skills</h3>
        <p>{readingSkill.data.name}</p>
      </div>
    );
  };

  return (
    <div css={rootCss(isCompact)}>
      {renderReadingSkill()}
      <div css={progressBarContainerCss(isCompact, hasScore)}>
        <ProgressBar current={currentItem} count={count} />
        {renderStandards()}
      </div>
      <div css={textContainerCss(isCompact)}>
        <h3
          css={questionCountContainerCss(isCompact)}
          ref={questionCounterTitleRef}
        >
          <span css={labelCss(isCompact)}>{item}</span>
          <div css={countContainerCss(isCompact)}>
            <div css={currentItemCss(isCompact)}>{currentItem}</div>
            <div css={countCss(isCompact)}>{` of ${count}`}</div>
          </div>
        </h3>
        {hasScore && (
          <h3 css={scoreIllustrationContainerCss}>
            <span css={scoreLabelCss}>{title}</span>
            <PowerWordsScoreGraphics
              isSubmitted={isSubmitted}
              currentItem={currentItem}
              powerWordsPoints={powerWordsPoints}
            />
          </h3>
        )}
      </div>
    </div>
  );
};

export default ItemCount;
